/* ============================================================
   NEO Offer Page - behaviour
   Vanilla JS, no dependencies, matches the theme's existing style
   (custom elements + Sections Rendering API + PUB_SUB_EVENTS).

   Three independent pieces:
     1. NeoVideos   - IntersectionObserver lazy play/pause for all UGC video
     2. NeoFaq      - accordion, one open at a time
     3. NeoOfferCard - the offer card: colour/size/bundle state, live
                       pricing from real variant prices, multi-line add to cart
   ============================================================ */

(function () {
  'use strict';

  /* This file is referenced by several NEO sections. A page that uses more
     than one of them gets more than one <script> element pointing here, and
     browsers execute each element separately — so everything below must be
     safe to run repeatedly. Each root element is stamped once via
     markReady(); the document-level listeners are registered once via the
     window flag at the bottom. */
  function markReady(el, key) {
    var attr = 'data-neo-ready-' + key;
    if (el.hasAttribute(attr)) return false;
    el.setAttribute(attr, '');
    return true;
  }

  /* ---------- 1. Lazy video ---------- */

  var NeoVideos = (function () {
    var io = null;

    function reducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function observer() {
      if (io || !('IntersectionObserver' in window)) return io;
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          // Gallery items are stacked on top of each other, so every one of
          // them intersects even though only one is visible. Only the active
          // one should decode - the gallery itself drives play/pause there.
          var hiddenGalleryItem = v.classList.contains('neo-gallery-item') &&
                                  !v.classList.contains('is-active');
          if (entry.isIntersecting && !hiddenGalleryItem) {
            if (v.preload !== 'auto') v.preload = 'auto';
            var p = v.play();
            if (p && p.catch) p.catch(function () { /* autoplay blocked - fine */ });
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: 0.15 });
      return io;
    }

    function scan(root) {
      var scope = root || document;
      var vids = scope.querySelectorAll('video[data-neo-ugc]:not([data-neo-bound])');
      Array.prototype.forEach.call(vids, function (v) {
        v.setAttribute('data-neo-bound', '');
        v.muted = true;
        v.playsInline = true;
        // Never autoplay when the visitor has asked for reduced motion.
        if (reducedMotion()) {
          v.setAttribute('controls', '');
          return;
        }
        var o = observer();
        if (o) o.observe(v);
      });
    }

    return { scan: scan };
  })();

  /* ---------- 2. FAQ accordion ---------- */

  function initFaq(root) {
    if (!markReady(root, 'faq')) return;
    var items = root.querySelectorAll('.neo-faq-item');
    if (!items.length) return;

    function close(item) {
      var btn = item.querySelector('.neo-faq-q');
      var body = item.querySelector('.neo-faq-a');
      btn.setAttribute('aria-expanded', 'false');
      btn.querySelector('i').textContent = '+';
      body.style.maxHeight = '0px';
    }

    function open(item) {
      var btn = item.querySelector('.neo-faq-q');
      var body = item.querySelector('.neo-faq-a');
      btn.setAttribute('aria-expanded', 'true');
      btn.querySelector('i').textContent = '–';
      // scrollHeight keeps the animation honest at any text length / viewport
      body.style.maxHeight = body.scrollHeight + 'px';
    }

    Array.prototype.forEach.call(items, function (item, i) {
      var btn = item.querySelector('.neo-faq-q');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        Array.prototype.forEach.call(items, close);
        if (!isOpen) open(item);
      });
      if (item.hasAttribute('data-neo-open')) open(item); else close(item);
    });

    // Re-measure the open panel when the viewport changes width.
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        Array.prototype.forEach.call(items, function (item) {
          var btn = item.querySelector('.neo-faq-q');
          if (btn && btn.getAttribute('aria-expanded') === 'true') open(item);
        });
      }, 150);
    });
  }

  /* ---------- 3. Gallery ---------- */

  function initGallery(root) {
    if (!markReady(root, 'gallery')) return;
    var thumbs = root.querySelectorAll('.neo-thumb');
    var items = root.querySelectorAll('.neo-gallery-item');
    if (!thumbs.length || !items.length) return;

    // A thumb points at a gallery item by index, because one thumb can stand
    // in for several items (the colour block emits one still per colour).
    function targetOf(thumb, fallback) {
      var raw = thumb.getAttribute('data-neo-thumb-target');
      var n = raw === null ? NaN : parseInt(raw, 10);
      return isNaN(n) ? fallback : n;
    }

    function show(index) {
      Array.prototype.forEach.call(items, function (el, i) {
        var on = i === index;
        el.classList.toggle('is-active', on);
        if (el.tagName === 'VIDEO') {
          if (on) {
            var p = el.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            el.pause();
          }
        }
      });
      // Highlight the thumb whose range covers the visible item: the last
      // thumb whose target index is <= the shown index.
      var best = -1, bestTarget = -1;
      Array.prototype.forEach.call(thumbs, function (t, i) {
        var target = targetOf(t, i);
        if (target <= index && target > bestTarget) { bestTarget = target; best = i; }
      });
      Array.prototype.forEach.call(thumbs, function (t, i) {
        t.setAttribute('aria-selected', i === best ? 'true' : 'false');
      });
    }

    Array.prototype.forEach.call(thumbs, function (t, i) {
      t.addEventListener('click', function () { show(targetOf(t, i)); });
    });

    root.neoShowGalleryColor = function (colorName) {
      // Jump the gallery to the still that matches the chosen colour, if one exists.
      var match = -1;
      Array.prototype.forEach.call(items, function (el, i) {
        if (match === -1 && el.getAttribute('data-neo-color') === colorName) match = i;
      });
      if (match > -1) show(match);
    };

    show(0);
  }

  /* ---------- 4. Offer card ---------- */

  function initOfferCard(root) {
    if (!markReady(root, 'card')) return;
    var dataEl = root.querySelector('[data-neo-data]');
    if (!dataEl) return;

    var DATA;
    try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var variants = DATA.variants || [];
    var colors = DATA.colors || [];
    var bundles = DATA.bundles || [];
    if (!variants.length || !colors.length || !bundles.length) return;

    var els = {
      price: root.querySelector('[data-neo-price]'),
      was: root.querySelector('[data-neo-was]'),
      badge: root.querySelector('[data-neo-savebadge]'),
      installment: root.querySelector('[data-neo-installment]'),
      colorName: root.querySelector('[data-neo-colorname]'),
      colorNote: root.querySelector('[data-neo-colornote]'),
      swatches: root.querySelectorAll('[data-neo-swatch]'),
      sizes: root.querySelectorAll('[data-neo-size]'),
      bundles: root.querySelectorAll('[data-neo-bundle]'),
      cta: root.querySelector('[data-neo-cta]'),
      error: root.querySelector('[data-neo-error]'),
      hh: root.querySelector('[data-neo-hh]'),
      mm: root.querySelector('[data-neo-mm]'),
      ss: root.querySelector('[data-neo-ss]')
    };

    // Largest bundle on offer. picks is always kept at this length so that
    // dropping from a 3-pack to a 2-pack and back restores the third mask's
    // colour instead of silently resetting it to the default.
    var maxQty = bundles.reduce(function (m, b) { return Math.max(m, b.qty); }, 1);

    var state = {
      size: DATA.defaultSize,
      bundleIndex: DATA.defaultBundleIndex || 0,
      picks: [],
      adding: false,
      added: false,
      note: ''
    };

    // Colour shown on mask 1 at the previous paint, so we can tell a real
    // colour change from a re-render. null until the first paint completes.
    var lastFeatured = null;

    function showMaskColor(color) {
      var page = root.closest('.neo-page') || document;
      var gallery = page.querySelector('[data-neo-gallery]');
      if (gallery && gallery.neoShowGalleryColor) gallery.neoShowGalleryColor(color);
    }

    /* -- helpers -- */

    function money(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) return '';
      var s = (cents / 100).toFixed(2);
      // Match the shape the design uses: $79.99
      return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function variantFor(color, size) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].color === color && variants[i].size === size) return variants[i];
      }
      return null;
    }

    function isSellable(color, size) {
      var v = variantFor(color, size);
      return !!(v && v.available);
    }

    function colorsAvailableAt(size) {
      return colors.filter(function (c) { return isSellable(c.name, size); });
    }

    function firstAvailableColor(size) {
      var list = colorsAvailableAt(size);
      return list.length ? list[0].name : colors[0].name;
    }

    function currentQty() {
      return bundles[state.bundleIndex].qty;
    }

    function currentPct() {
      var p = bundles[state.bundleIndex].discount_percent;
      return typeof p === 'number' ? p : 0;
    }

    /* Keep picks at full length, replacing only those colours that genuinely
       cannot be sold at the current size (e.g. Rose Gold has no M/L variant).
       Choices for masks outside the current bundle are preserved. */
    function normalisePicks() {
      var fallback = firstAvailableColor(state.size);
      for (var i = 0; i < maxQty; i++) {
        var c = state.picks[i];
        if (!isSellable(c, state.size)) state.picks[i] = fallback;
      }
      state.picks.length = maxQty;
    }

    /* The colours actually being bought right now. */
    function activePicks() {
      return state.picks.slice(0, currentQty());
    }

    /* Group picks into { color: qty } so the cart gets one line per colour. */
    function grouped() {
      var map = {};
      activePicks().forEach(function (c) { map[c] = (map[c] || 0) + 1; });
      return map;
    }

    /* Subtotal at normal price, for the exact colours chosen. */
    function subtotalFor(qty, picks) {
      var total = 0;
      var ok = true;
      for (var i = 0; i < qty; i++) {
        var v = variantFor(picks[i], state.size);
        if (!v) { ok = false; continue; }
        total += v.price;
      }
      return ok ? total : null;
    }

    /* Shopify computes a percentage discount PER UNIT and truncates it to the
       cent, then multiplies by quantity — it does not round the line total.
       30% of $79.99 is $23.997, which becomes $23.99 off each mask, so a
       3-pack saves $71.97 rather than the $71.99 you get by discounting
       $239.97 in one go. Verified against live checkout across same-colour,
       two-colour and three-colour carts; mirror it exactly or the page
       advertises a price a couple of cents off what is actually charged. */
    function discountedTotal(pct, picksForQty) {
      var map = {};
      picksForQty.forEach(function (c) { map[c] = (map[c] || 0) + 1; });
      var total = 0;
      for (var color in map) {
        if (!Object.prototype.hasOwnProperty.call(map, color)) continue;
        var v = variantFor(color, state.size);
        if (!v) return null;
        // epsilon guards against 2399.9999999 style float error before floor
        var perUnitOff = Math.floor((v.price * pct / 100) + 1e-6);
        total += (v.price - perUnitOff) * map[color];
      }
      return total;
    }

    /* Price shown on each bundle row. Uses the colours that would actually
       apply if the shopper picked that row, so the row price never disagrees
       with the headline once selected. */
    function previewFor(bundle) {
      var picks = state.picks.slice(0, bundle.qty);
      while (picks.length < bundle.qty) picks.push(firstAvailableColor(state.size));
      var was = subtotalFor(bundle.qty, picks);
      if (was === null) return null;
      var now = discountedTotal(bundle.discount_percent || 0, picks);
      return { was: was, now: now };
    }

    /* -- render -- */

    function render() {
      normalisePicks();

      var qty = currentQty();
      var pct = currentPct();
      var active = activePicks();
      var was = subtotalFor(qty, active);
      var now = discountedTotal(pct, active);
      var featured = state.picks[0];

      // Headline price
      if (els.price) els.price.textContent = now === null ? '—' : money(now);
      if (els.was) {
        var showWas = was !== null && now !== null && was > now;
        els.was.textContent = showWas ? money(was) : '';
        els.was.style.display = showWas ? '' : 'none';
      }
      if (els.badge) {
        var save = (was !== null && now !== null) ? was - now : 0;
        els.badge.textContent = save > 0 ? 'Bundle · Save ' + money(save) : '';
      }
      if (els.installment && now !== null) {
        els.installment.textContent = money(Math.round(now / 4));
      }

      // Colour label + swatches
      if (els.colorName) els.colorName.textContent = featured;

      Array.prototype.forEach.call(els.swatches, function (btn) {
        var name = btn.getAttribute('data-neo-swatch');
        var sellable = isSellable(name, state.size);
        /* aria-disabled rather than the disabled property: a genuinely
           disabled button fires no click at all, so it could never explain
           itself. This stays focusable and tappable, and answers when used. */
        btn.setAttribute('aria-disabled', sellable ? 'false' : 'true');
        btn.setAttribute('aria-pressed', name === featured ? 'true' : 'false');
        btn.title = sellable ? name : unavailableReason(name, state.size);
      });

      /* The unavailable note is shown only when a shopper actually reaches for
         that colour. Printing it permanently put a line of small print under
         the swatches on every colour and every size, which is noise. */
      if (els.colorNote) els.colorNote.textContent = state.note || '';

      // Sizes
      Array.prototype.forEach.call(els.sizes, function (btn) {
        var val = btn.getAttribute('data-neo-size');
        btn.setAttribute('aria-pressed', val === state.size ? 'true' : 'false');
      });

      // Bundle rows
      Array.prototype.forEach.call(els.bundles, function (row, i) {
        var on = i === state.bundleIndex;
        row.classList.toggle('is-on', on);
        var radio = row.querySelector('[role="radio"]') || row;
        radio.setAttribute('aria-checked', on ? 'true' : 'false');

        var preview = previewFor(bundles[i]);
        var priceEl = row.querySelector('[data-neo-bundle-price]');
        var wasEl = row.querySelector('[data-neo-bundle-was]');
        var tagEl = row.querySelector('[data-neo-bundle-tag]');

        if (priceEl) priceEl.textContent = preview ? money(preview.now) : '—';
        if (wasEl) {
          var show = preview && preview.was > preview.now;
          wasEl.textContent = show ? money(preview.was) : '';
          wasEl.style.display = show ? '' : 'none';
        }
        if (tagEl) {
          var saved = preview ? preview.was - preview.now : 0;
          tagEl.textContent = saved > 0 ? 'Save ' + money(saved) : '';
          tagEl.style.display = saved > 0 ? '' : 'none';
        }

        // Per-mask colour selects, only on the selected row
        if (on) renderPicks(row);
      });

      // CTA
      if (els.cta) {
        if (state.added) {
          els.cta.textContent = DATA.ctaAdded || '✓ It’s hers';
          els.cta.classList.add('is-added');
          els.cta.disabled = true;
        } else if (state.adding) {
          els.cta.textContent = DATA.ctaAdding || 'Adding…';
          els.cta.classList.remove('is-added');
          els.cta.disabled = true;
        } else {
          els.cta.textContent = DATA.ctaLabel || 'Yes — lock it in';
          els.cta.classList.remove('is-added');
          els.cta.disabled = now === null;
        }
      }

      // Keep the hero gallery in sync with mask 1 — but only once the shopper
      // has actually changed colour. Syncing on the first paint would yank the
      // gallery off the opening video and onto a colour still, and syncing on
      // every render would drag them back whenever they picked a thumb.
      if (lastFeatured !== null && lastFeatured !== featured) {
        showMaskColor(featured);
      }
      lastFeatured = featured;
    }

    /* Sold out and never-made are different facts and shoppers read them
       differently: one might come back, the other never will. */
    function unavailableReason(color, size) {
      var v = variantFor(color, size);
      if (!v) return color + ' is not made in ' + sizeLabel(size) + '.';
      if (!v.available) return color + ' is sold out in ' + sizeLabel(size) + '.';
      return color;
    }

    function sizeLabel(value) {
      for (var i = 0; i < (DATA.sizes || []).length; i++) {
        if (DATA.sizes[i].value === value) return DATA.sizes[i].label;
      }
      return value;
    }

    function hexOf(name) {
      var c = colors.filter(function (x) { return x.name === name; })[0];
      return c ? c.hex : '#CCCCCC';
    }

    function closeAllPickLists(except) {
      Array.prototype.forEach.call(root.querySelectorAll('.neo-pick'), function (f) {
        if (f === except) return;
        f.classList.remove('is-open');
        var t = f.querySelector('.neo-pick-trigger');
        var l = f.querySelector('.neo-pick-list');
        if (t) t.setAttribute('aria-expanded', 'false');
        // The panel is hidden by the [hidden] attribute, not by the class, so
        // dropping is-open alone left it on screen after an outside click.
        if (l) l.hidden = true;
      });
    }

    /* A native <select> cannot render a colour dot inside its options, so the
       picker is a custom listbox: a trigger showing dot + name, and a panel of
       plain white rows each showing dot + name. Keyboard operable. */
    function buildPick(index) {
      var field = document.createElement('div');
      field.className = 'neo-pick';

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'neo-pick-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      /* The accessible name has to contain the visible text, or voice control
         users saying "click Black" hit a button announced as something else
         (WCAG 2.5.3). So the mask number is a visually hidden prefix rather
         than an aria-label that replaces the name outright. */
      trigger.innerHTML =
        '<i aria-hidden="true"></i>' +
        '<span class="neo-sr">Mask ' + (index + 1) + ' colour: </span>' +
        '<span data-neo-pick-name></span>';

      var list = document.createElement('div');
      list.className = 'neo-pick-list';
      list.setAttribute('role', 'listbox');
      list.hidden = true;

      function choose(name) {
        state.picks[index] = name;
        state.note = '';
        state.added = false;
        field.classList.remove('is-open');
        list.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
        render();
        showMaskColor(name);
      }

      colorsAvailableAt(state.size).forEach(function (c) {
        var opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'neo-pick-opt';
        opt.setAttribute('role', 'option');
        opt.setAttribute('data-neo-color', c.name);
        opt.innerHTML = '<i aria-hidden="true" style="--dot:' + c.hex + '"></i><span></span>';
        opt.querySelector('span').textContent = c.name;
        opt.addEventListener('click', function (e) { e.stopPropagation(); choose(c.name); });
        list.appendChild(opt);
      });

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = field.classList.contains('is-open');
        closeAllPickLists();
        if (open) {
          field.classList.remove('is-open');
          list.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          field.classList.add('is-open');
          list.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          var sel = list.querySelector('[aria-selected="true"]') || list.firstChild;
          if (sel) sel.focus();
        }
      });

      list.addEventListener('keydown', function (e) {
        var opts = Array.prototype.slice.call(list.children);
        var i = opts.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var next = e.key === 'ArrowDown' ? i + 1 : i - 1;
          if (next < 0) next = opts.length - 1;
          if (next >= opts.length) next = 0;
          opts[next].focus();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          field.classList.remove('is-open');
          list.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      });

      field.appendChild(trigger);
      field.appendChild(list);
      return field;
    }

    function renderPicks(row) {
      var wrap = row.querySelector('[data-neo-picks]');
      if (!wrap) return;
      var qty = currentQty();

      // Rebuild when the mask count or the sellable colour set changes.
      if (wrap.childElementCount !== qty || wrap.getAttribute('data-neo-size-key') !== state.size) {
        wrap.innerHTML = '';
        wrap.setAttribute('data-neo-size-key', state.size);
        for (var i = 0; i < qty; i++) wrap.appendChild(buildPick(i));
      }

      Array.prototype.forEach.call(wrap.children, function (field, i) {
        var name = state.picks[i];
        var trigger = field.querySelector('.neo-pick-trigger');
        trigger.querySelector('i').style.setProperty('--dot', hexOf(name));
        trigger.querySelector('[data-neo-pick-name]').textContent = name;
        Array.prototype.forEach.call(field.querySelectorAll('.neo-pick-opt'), function (o) {
          o.setAttribute('aria-selected', o.getAttribute('data-neo-color') === name ? 'true' : 'false');
        });
      });
    }

    /* -- events -- */

    Array.prototype.forEach.call(els.swatches, function (btn) {
      btn.addEventListener('click', function () {
        var name = btn.getAttribute('data-neo-swatch');
        if (!isSellable(name, state.size)) {
          // Answer at the moment they reach for it, then get out of the way.
          state.note = unavailableReason(name, state.size);
          render();
          return;
        }
        state.note = '';
        state.picks[0] = name;
        state.added = false;
        render();
        showMaskColor(name);
      });
    });

    Array.prototype.forEach.call(els.sizes, function (btn) {
      btn.addEventListener('click', function () {
        state.size = btn.getAttribute('data-neo-size');
        state.note = '';
        state.added = false;
        render();
      });
    });

    Array.prototype.forEach.call(els.bundles, function (row, i) {
      row.addEventListener('click', function (e) {
        // Clicks inside a colour select must not re-trigger row selection.
        if (e.target.closest('.neo-pick')) return;
        state.bundleIndex = i;
        state.added = false;
        render();
      });
      row.addEventListener('keydown', function (e) {
        if (e.target.closest('.neo-pick')) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          state.bundleIndex = i;
          state.added = false;
          render();
        }
      });
    });

    // Any click outside a colour picker closes it. Pointerdown as well as
    // click, so it also dismisses on touch and on right-click.
    function outsideClose(e) {
      var t = e.target;
      if (!t || !t.closest || !t.closest('.neo-pick')) closeAllPickLists();
    }
    document.addEventListener('click', outsideClose);
    document.addEventListener('pointerdown', outsideClose);

    // Escape closes it from anywhere, not just when a list item has focus.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAllPickLists();
    });


    /* -- add to cart -- */

    function addToCart() {
      if (state.adding) return;
      var map = grouped();
      var bad = null;
      var chosen = [];

      /* Absolute quantities, not an add. Every variant of this product is
         named explicitly — the chosen colours at their quantity, everything
         else at zero — so clicking the CTA always leaves the cart holding
         exactly the selected bundle. Without this, a shopper who reaches
         checkout, presses Back and clicks again ends up with six masks.
         Other products in the cart are untouched. */
      var updates = {};
      variants.forEach(function (v) { updates[v.id] = 0; });

      for (var color in map) {
        if (!Object.prototype.hasOwnProperty.call(map, color)) continue;
        var v = variantFor(color, state.size);
        if (!v || !v.available) { bad = color; break; }
        updates[v.id] = map[color];
        chosen.push(v.id);
      }

      if (bad || !chosen.length) {
        if (els.error) {
          els.error.textContent = bad
            ? bad + ' is not available in ' + sizeLabel(state.size) + '. Pick another colour or size.'
            : 'Something went wrong selecting that combination.';
        }
        return;
      }

      var items = [{ id: chosen[0] }];

      if (els.error) els.error.textContent = '';
      state.adding = true;
      render();

      // Safety net: if navigation is blocked or the request hangs, give the
      // button back rather than leaving it dead.
      clearTimeout(addToCart._t);
      addToCart._t = setTimeout(function () {
        if (state.adding) { state.adding = false; render(); }
      }, 12000);

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function (r) {
          return r.json().then(function (body) {
            if (!r.ok) throw new Error(body.description || body.message || 'Add to cart failed');
            return body;
          });
        })
        .then(function () {
          // The bundle price this page advertises is only real once the tier's
          // discount code is applied. Automatic discounts would apply store-wide;
          // a code applied here keeps the offer scoped to this page's traffic.
          var code = (bundles[state.bundleIndex] || {}).code || '';

          // Straight to checkout: keep the button in its busy state so it
          // cannot be double-tapped while the browser navigates away.
          if (DATA.afterAdd === 'checkout') {
            if (els.cta) els.cta.textContent = DATA.ctaCheckout || 'Taking you to checkout…';
            window.location.href = code
              ? '/checkout?discount=' + encodeURIComponent(code)
              : '/checkout';
            return;
          }

          // Not navigating to checkout, so park the code on the session first;
          // Shopify then honours it whenever they do check out.
          if (code) {
            fetch('/discount/' + encodeURIComponent(code), { method: 'GET' }).catch(function () {});
          }

          state.adding = false;
          state.added = true;
          render();

          // Tell the theme (and the Oxify drawer, which listens on the
          // document) that the cart changed, then refresh cart sections.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'neo-offer-card',
              productVariantId: items[0].id
            });
          }

          if (DATA.afterAdd === 'cart') {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          setTimeout(function () {
            if (state.added) { state.added = false; render(); }
          }, 1800);
        })
        .catch(function (err) {
          state.adding = false;
          state.added = false;
          render();
          if (els.error) {
            els.error.textContent = (err && err.message)
              ? err.message
              : 'We could not add that to the cart. Please try again.';
          }
        });
    }

    if (els.cta) els.cta.addEventListener('click', addToCart);

    /* Pressing Back from checkout restores this page from the browser's
       back-forward cache with all JS state frozen, which left the CTA stuck
       on "Taking you to checkout…" and permanently disabled. Nothing re-runs
       on a bfcache restore except pageshow, so reset the button there. */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      clearTimeout(addToCart._t);
      state.adding = false;
      state.added = false;
      render();
    });

    // Same situation on browsers that only fire visibilitychange on restore.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && state.adding) {
        clearTimeout(addToCart._t);
        state.adding = false;
        render();
      }
    });

    /* -- countdown -- */

    if (DATA.countdownSeconds > 0 && els.hh) {
      var KEY = 'neo-countdown-' + (DATA.sectionId || 'default');
      var endsAt;
      try {
        var stored = window.sessionStorage.getItem(KEY);
        endsAt = stored ? parseInt(stored, 10) : 0;
      } catch (e) { endsAt = 0; }

      var nowMs = Date.now();
      if (!endsAt || endsAt < nowMs) {
        endsAt = nowMs + DATA.countdownSeconds * 1000;
        try { window.sessionStorage.setItem(KEY, String(endsAt)); } catch (e) {}
      }

      var pad = function (n) { return String(n).padStart(2, '0'); };
      var tick = function () {
        var left = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
        els.hh.textContent = pad(Math.floor(left / 3600));
        els.mm.textContent = pad(Math.floor((left % 3600) / 60));
        els.ss.textContent = pad(left % 60);
        if (left <= 0 && timer) clearInterval(timer);
      };
      var timer = setInterval(tick, 1000);
      tick();
    }

    // Seed every mask slot with the default colour, then paint.
    state.picks = [];
    for (var seed = 0; seed < maxQty; seed++) state.picks.push(DATA.defaultColor);
    render();
  }

  /* ---------- boot ---------- */

  function boot(scope) {
    var root = scope || document;
    NeoVideos.scan(root);
    Array.prototype.forEach.call(root.querySelectorAll('[data-neo-faq]'), initFaq);
    Array.prototype.forEach.call(root.querySelectorAll('[data-neo-gallery]'), initGallery);
    Array.prototype.forEach.call(root.querySelectorAll('[data-neo-offer-card]'), initOfferCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Register the document-level listeners only once per page, however many
  // section script tags loaded this file.
  if (!window.__neoOfferListeners) {
    window.__neoOfferListeners = true;
    // Theme editor: re-init whichever section was just re-rendered.
    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  }
})();
